import { getRandomInt } from "../Shared/Helpers.tsx";
import { ProblemData } from "./Constants.ts";
import { FactLadder, levelName, reviewFacts } from "./FactLevels.ts";
import type { LevelLanguage } from "./FactLevels.ts";
import { problemFromFactKey } from "./FactKeys.ts";

/**
 * The fact finder of a placement check (ADR 0086 §3): one fact from each level of a ladder, in
 * ladder order, on one sheet. The first fact a child misses is the level to start at.
 *
 * Which fact stands for a level matters, and it is not any fact in it. A times table's focus holds the
 * whole table, both ways round, so "3 x 7" is in the 3s and in the 7s: a child who misses it may only
 * not know the 7s. So the fact drawn for a level is one **new at that level** (in its focus and in no
 * earlier level's). The addition sets never overlap, so for them every fact is new.
 *
 * The last level, Mixed, is every fact together, so it has none of its own. It draws one of the
 * facts new at the last three levels before it - the hardest, since the ladder is ordered easiest
 * first - rather than any fact: a mixed item that was ×2 would end the finder on its easiest one.
 *
 * Dealt, not drawn, as 0071 §3 requires of a level test: exactly one fact per level, none repeated,
 * so a child who gets to the end has seen every level once. All randomness goes through `getRandomInt`,
 * so it reproduces from the seed the caller has open (ADR 0009).
 */
export function factFinderKeys(ladder: FactLadder): string[] {
    const chosen: string[] = [];
    const seen = new Set<string>();
    for (const level of ladder.levels) {
        const earlier = new Set(reviewFacts(ladder, level.id));
        const fresh = level.focus.filter((fact) => !earlier.has(fact) && !seen.has(fact));
        const pool = fresh.length > 0 ? fresh : hardest(ladder, seen);
        const fact = pool[getRandomInt(0, pool.length - 1)];
        seen.add(fact);
        chosen.push(fact);
    }
    return chosen;
}

/** The facts of the three levels before the last that nothing has drawn yet; every unseen fact if none. */
function hardest(ladder: FactLadder, seen: Set<string>): string[] {
    const last = ladder.levels.length - 1;
    const hard = new Set(ladder.levels.slice(Math.max(0, last - 3), last).flatMap((level) => level.focus));
    const pool = [...hard].filter((fact) => !seen.has(fact));
    return pool.length > 0 ? pool : ladder.levels[last].focus.filter((fact) => !seen.has(fact));
}

/** The fact in print: "7 x 4" is written "7 × 4". */
export function factPrompt(key: string): string {
    return key.replace("x", "×").replace(/([+\-×÷])/, " $1 ") + " =";
}

/**
 * The fact finder as the page data of a numbered sheet: item 1 is level 1, item 2 level 2, and so on.
 * Each problem is a prompt and its answer, which `GeneratePromptAndAnswerProblem` prints as
 * "3. 7 × 4 =" with a rule to answer on (and the answer on the key).
 */
export function factFinderPage(ladder: FactLadder): ProblemData[] {
    return factFinderKeys(ladder).map((key, index) => {
        const base = problemFromFactKey(key, index + 1)!;
        return { ...base, display: [factPrompt(key), String(base.numbers[2])] };
    });
}

/** What the key prints beside each item: its number, and the level it stands for. */
export function factFinderLevels(ladder: FactLadder, ladderId: Parameters<typeof levelName>[1], language: LevelLanguage = "en") {
    return ladder.levels.map((level, index) => ({ item: index + 1, level: level.id, label: levelName(level, ladderId, language) }));
}
